import { Leaf, Globe2, HeartHandshake, Truck, Sparkles, Gift } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { IMG, PHOTO } from "@/lib/content";
import { SplitText, Reveal } from "@/components/fx/Motion";
import { PartnerForm } from "@/components/shop/PartnerForm";

const VALUES = [[Leaf, "about_v1"], [Globe2, "about_v2"], [HeartHandshake, "about_v3"]];
const PARTNERS = [[Truck, "about_p1"], [Sparkles, "about_p2"], [Gift, "about_p3"]];

const Company = () => {
  const { t } = useI18n();
  const rows = [[t("about_company_name"), "AJS Luxury FZCO"], [t("about_company_reg"), "Dubai, United Arab Emirates"], [t("about_company_licence"), "63259"]];
  return (
    <Reveal className="border border-[#c9a55c]/30 p-8 lg:p-10 bg-white/[0.02]">
      <p className="eyebrow mb-4">{t("about_company_title")}</p>
      <p className="text-white/70 mb-8 max-w-md">{t("about_company_text")}</p>
      <dl data-testid="about-company-details" className="divide-y divide-white/10">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 py-3 text-sm"><dt className="text-white/50">{k}</dt><dd className="text-white text-right">{v}</dd></div>
        ))}
      </dl>
    </Reveal>
  );
};

export default function About() {
  const { t } = useI18n();
  useSeo(t("about_seo_title"), t("about_intro"));
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
        <div>
          <p className="eyebrow mb-5">{t("about_eyebrow")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-8"><SplitText text={t("about_title")} /></h1>
          <p className="text-white/70 text-base lg:text-lg max-w-xl leading-relaxed">{t("about_intro")}</p>
        </div>
        <Reveal className="relative aspect-[4/5] overflow-hidden">
          <img src={PHOTO.dayCream2} alt="AJS Luxury" className="h-full w-full object-cover" />
          <img src={IMG.logo} alt="" className="absolute bottom-5 left-5 h-20 w-20 object-contain bg-[#0b0a08]/70 backdrop-blur-md p-2" />
        </Reveal>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-16 lg:pb-24">
        <p className="eyebrow mb-8">{t("about_values_title")}</p>
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {VALUES.map(([Icon, k], i) => (
            <Reveal key={k} delay={i * 0.1} className="bg-[#0b0a08] p-8">
              <Icon className="text-gold mb-5" size={26} strokeWidth={1.4} />
              <h3 className="font-display text-2xl mb-3">{t(`${k}_t`)}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{t(k)}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section id="partners" className="border-t border-white/5 bg-[#080706]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="eyebrow mb-5">B2B</p>
            <h2 className="font-display text-3xl lg:text-5xl mb-6">{t("about_partner_title")}</h2>
            <p className="text-white/70 mb-8 max-w-md">{t("about_partner_text")}</p>
            <ul className="space-y-4 mb-12">
              {PARTNERS.map(([Icon, k]) => <li key={k} className="flex items-center gap-4 text-sm"><Icon size={18} className="text-gold shrink-0" />{t(k)}</li>)}
            </ul>
            <Company />
          </div>
          <div className="lg:pt-24"><PartnerForm /></div>
        </div>
      </section>
    </div>
  );
}
